import { Button } from "@/components/ui/button";
import { SignUpForm } from "./auth/SignUpForm";
import { SignInForm } from "./auth/SignInForm";
import { ForgotPasswordForm } from "./auth/ForgotPasswordForm";
import { useAuthForm } from "@/hooks/auth/use-auth-form";
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { AuthModeSelector } from "./auth/AuthModeSelector";
import { FormHeader } from "./auth/FormHeader";
import { AuthWarning } from "./auth/AuthWarning";
import { PasswordUpdateManager } from "./auth/PasswordUpdateManager";
import { useEmailFetcher } from "./auth/EmailFetcher";

interface AuthFormProps {
  initialMode?: boolean;
  isPasswordReset?: boolean;
  initialForgotPassword?: boolean;
}

export const AuthForm = ({ 
  initialMode = false, 
  isPasswordReset = false, 
  initialForgotPassword = false 
}: AuthFormProps) => {
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode');
  
  // Set form mode based on URL parameters
  const [localIsForgotPassword, setLocalIsForgotPassword] = useState(
    initialForgotPassword || mode === 'forgot-password'
  );
  const isUpdatePassword = mode === 'update-password' || isPasswordReset;

  // Get user email for password reset
  const [userEmail, setUserEmail] = useState<string | null>(null);
  useEmailFetcher({ 
    isUpdatePassword, 
    onEmailFetched: setUserEmail 
  });

  // Get form state and handlers
  const {
    email,
    setEmail,
    password,
    setPassword,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    country,
    setCountry,
    acceptedTerms,
    setAcceptedTerms,
    isLoading,
    isSignUp,
    setIsSignUp,
    handleAuth,
    handleResetPassword,
    setIsForgotPassword: setAuthFormForgotPassword
  } = useAuthForm(initialMode);

  // Keep the two forgot password states in sync
  useEffect(() => {
    setLocalIsForgotPassword(initialForgotPassword || mode === 'forgot-password');
    setAuthFormForgotPassword(initialForgotPassword || mode === 'forgot-password');
  }, [initialForgotPassword, mode, setAuthFormForgotPassword]);

  useEffect(() => {
    setAuthFormForgotPassword(localIsForgotPassword);
  }, [localIsForgotPassword, setAuthFormForgotPassword]);

  // Determine the title and description based on the current form mode
  const getFormMeta = () => {
    if (isUpdatePassword) {
      return {
        title: "Update Password",
        description: `Set a new password for ${userEmail || 'your account'}`
      };
    }
    if (localIsForgotPassword) {
      return {
        title: "Forgot Password",
        description: "Reset your password"
      };
    }
    if (isSignUp) {
      return {
        title: "Create Account",
        description: "Sign up to start your adventure"
      };
    }
    return {
      title: "Welcome Back",
      description: "Sign in to continue your quest"
    };
  };
  
  const formMeta = getFormMeta();

  return (
    <div className="w-full max-w-md space-y-8 bg-white/10 p-8 rounded-lg backdrop-blur-sm">
      <FormHeader title={formMeta.title} description={formMeta.description} />

      {isUpdatePassword ? (
        <PasswordUpdateManager
          password={password}
          setPassword={setPassword}
          userEmail={userEmail}
        />
      ) : localIsForgotPassword ? (
        <ForgotPasswordForm 
          email={email}
          setEmail={setEmail}
          onSubmit={handleResetPassword}
          isLoading={isLoading}
        />
      ) : (
        <form onSubmit={handleAuth} className="mt-8 space-y-6">
          {isSignUp ? (
            <SignUpForm
              firstName={firstName}
              setFirstName={setFirstName}
              lastName={lastName}
              setLastName={setLastName}
              country={country}
              setCountry={setCountry}
              email={email}
              setEmail={setEmail}
              password={password}
              setPassword={setPassword}
              acceptedTerms={acceptedTerms}
              setAcceptedTerms={setAcceptedTerms}
            />
          ) : (
            <SignInForm
              email={email}
              setEmail={setEmail}
              password={password}
              setPassword={setPassword}
              onForgotPassword={() => setLocalIsForgotPassword(true)}
            />
          )}

          {!isSignUp && !localIsForgotPassword && <AuthWarning />}

          <Button
            type="submit"
            className="w-full bg-gameGold text-gamePurple hover:bg-gameGold/90"
            disabled={isLoading}
          >
            {isLoading
              ? "Please wait..."
              : isSignUp
              ? "Create Account"
              : "Sign In"}
          </Button>

          {!localIsForgotPassword && !isUpdatePassword && (
            <AuthModeSelector isSignUp={isSignUp} setIsSignUp={setIsSignUp} />
          )}
        </form>
      )}
    </div>
  );
};
